// Mobile Navigation Component
// Provides hamburger menu and bottom navigation bar for mobile devices
// Optimized for iOS and Android with safe area support

import React, { useState, useEffect, lazy, Suspense } from 'react';

const SubscriptionManagement = lazy(() =>
  import('./SubscriptionManagement.js').then((m) => ({ default: m.SubscriptionManagement || m.default }))
);

const MobileNavigation = ({ currentView, setCurrentView, calculators = [] }) => {
    const [showBilling, setShowBilling] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    // Close menu on view change
    useEffect(() => {
        setMenuOpen(false);
    }, [currentView]);

    // Prevent body scroll when menu open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [menuOpen]);

    const handleNavClick = (view) => {
        setCurrentView(view);
        setMenuOpen(false);
        // Scroll to top when navigating
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            {/* Top Header */}
            <header className="fixed top-0 left-0 right-0 bg-white border-b z-50 shadow-sm" 
                    style={{ paddingTop: 'max(env(safe-area-inset-top), 0px)' }}>
                <div className="flex items-center justify-between px-4 py-3">
                    {/* Logo */}
                    <div className="flex items-center cursor-pointer" onClick={() => handleNavClick('home')}>
                        <div className="text-xl font-bold text-blue-600">SuperCalc Pro</div>
                    </div>
                    
                    {/* Hamburger Menu Button */}
                    <button 
                        onClick={() => setMenuOpen(!menuOpen)}
                        className="p-2 rounded-lg hover:bg-gray-100 touch-friendly"
                        style={{ minWidth: '44px', minHeight: '44px' }}
                        aria-label="Menu"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {menuOpen ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </svg>
                    </button>
                </div>
            </header>

            {/* Slide-out Menu */}
            <div className={`fixed inset-0 z-40 transform transition-transform duration-300 ease-in-out ${
                menuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                {/* Backdrop */}
                <div 
                    className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={() => setMenuOpen(false)}
                    style={{ opacity: menuOpen ? 1 : 0 }}
                />
                
                {/* Menu Content */}
                <div className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white overflow-y-auto shadow-2xl"
                     style={{ 
                         paddingTop: 'max(env(safe-area-inset-top), 0px)',
                         paddingRight: 'max(env(safe-area-inset-right), 0px)',
                         paddingBottom: 'max(env(safe-area-inset-bottom), 0px)'
                     }}>
                    {/* Menu Header */}
                    <div className="p-6 border-b">
                        <h2 className="text-xl font-bold text-gray-900">Menu</h2>
                    </div>
                    
                    {/* Menu Items */}
                    <nav className="p-4">
                        {/* Calculators Section */}
                        <div className="mb-6">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Calculators</h3>
                            <div className="space-y-2">
                                <button 
                                    onClick={() => handleNavClick('basic')}
                                    className={`w-full text-left p-4 rounded-lg flex items-center transition-colors ${
                                        currentView === 'basic' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                    style={{ minHeight: '60px' }}
                                >
                                    <span className="text-2xl mr-3">📊</span>
                                    <div className="flex-1">
                                        <div className="font-medium">Basic Calculator</div>
                                        <div className="text-xs opacity-75">Quick retirement estimate</div>
                                    </div>
                                </button>
                                
                                <button 
                                    onClick={() => handleNavClick('accumulation')}
                                    className={`w-full text-left p-4 rounded-lg flex items-center transition-colors ${
                                        currentView === 'accumulation' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                    style={{ minHeight: '60px' }}
                                >
                                    <span className="text-2xl mr-3">💰</span>
                                    <div className="flex-1">
                                        <div className="font-medium">Accumulation Calculator</div>
                                        <div className="text-xs opacity-75">Grow your super</div>
                                    </div>
                                </button>
                                
                                <button 
                                    onClick={() => handleNavClick('advanced')}
                                    className={`w-full text-left p-4 rounded-lg flex items-center transition-colors ${
                                        currentView === 'advanced' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                    style={{ minHeight: '60px' }}
                                >
                                    <span className="text-2xl mr-3">🎯</span>
                                    <div className="flex-1">
                                        <div className="font-medium">Advanced Calculator</div>
                                        <div className="text-xs opacity-75">Comprehensive analysis</div>
                                        <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded mt-1">PRO</span>
                                    </div>
                                </button>
                                
                                <button 
                                    onClick={() => handleNavClick('smsf')}
                                    className={`w-full text-left p-4 rounded-lg flex items-center transition-colors ${
                                        currentView === 'smsf' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                    style={{ minHeight: '60px' }}
                                >
                                    <span className="text-2xl mr-3">🏢</span>
                                    <div className="flex-1">
                                        <div className="font-medium">SMSF Suite</div>
                                        <div className="text-xs opacity-75">36 specialized tools</div>
                                        <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded mt-1">PRO</span>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="mb-6">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Quick Links</h3>
                            <div className="space-y-2">
                                <a
                                    href="/blog/"
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center"
                                    style={{ minHeight: '48px' }}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    <span className="text-xl mr-3">📖</span>
                                    <span className="font-medium">Blog</span>
                                </a>
                                <button 
                                    onClick={() => handleNavClick('selection-guide')}
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center"
                                    style={{ minHeight: '48px' }}
                                >
                                    <span className="mr-3">📚</span>
                                    <span>Calculator Guide</span>
                                </button>
                                
                                <button 
                                    onClick={() => handleNavClick('pricing')}
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center"
                                    style={{ minHeight: '48px' }}
                                >
                                    <span className="mr-3">💳</span>
                                    <span>Pricing</span>
                                </button>
                            </div>
                        </div>

                        {/* Support Links */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Support</h3>
                            <div className="space-y-2">
                                <button 
                                    onClick={() => handleNavClick('help')}
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center text-sm"
                                    style={{ minHeight: '48px' }}
                                >
                                    Help Center
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        setShowBilling(true);
                                    }}
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center text-sm"
                                    style={{ minHeight: '48px' }}
                                >
                                    Billing &amp; subscription
                                </button>
                                <a 
                                    href="/guides/"
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center text-sm font-medium"
                                    style={{ minHeight: '48px' }}
                                >
                                    📚 Planning Guides
                                </a>
                            </div>
                        </div>

                        {/* Legal Links */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Legal</h3>
                            <div className="space-y-2">
                                <button 
                                    onClick={() => handleNavClick('privacy')}
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center text-sm"
                                    style={{ minHeight: '48px' }}
                                >
                                    Privacy Policy
                                </button>
                                
                                <button 
                                    onClick={() => handleNavClick('terms')}
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center text-sm"
                                    style={{ minHeight: '48px' }}
                                >
                                    Terms of Service
                                </button>
                                
                                <button 
                                    onClick={() => handleNavClick('disclaimer')}
                                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center text-sm"
                                    style={{ minHeight: '48px' }}
                                >
                                    Disclaimer
                                </button>
                            </div>
                        </div>
                    </nav>
                </div>
            </div>

            {/* Bottom Navigation Bar — plain inline styles, immune to Tailwind purge */}
            <nav style={{
                position:'fixed', bottom:0, left:0, right:0, zIndex:50,
                background:'#ffffff', borderTop:'2px solid #e5e7eb',
                boxShadow:'0 -2px 8px rgba(0,0,0,0.10)',
                display:'flex', alignItems:'stretch',
                paddingBottom:'max(env(safe-area-inset-bottom), 6px)',
                paddingTop:'6px',
                paddingLeft:'max(env(safe-area-inset-left), 4px)',
                paddingRight:'max(env(safe-area-inset-right), 4px)'
            }}>
                {[
                    { label:'Home',  active: currentView==='home', onClick:()=>handleNavClick('home'),
                      icon:<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/> },
                    { label:'Calc',  active: ['basic','accumulation','advanced','smsf'].includes(currentView), onClick:()=>handleNavClick('basic'),
                      icon:<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/> },
                    { label:'Blog',  active: false, isLink:true, href:'/blog/',
                      icon:<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/> },
                    { label:'More',  active: menuOpen, onClick:()=>setMenuOpen(!menuOpen),
                      icon:<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/> }
                ].map(item => {
                    const color = item.active ? '#2563eb' : '#374151';
                    const bg   = item.active ? '#eff6ff' : 'transparent';
                    const shared = { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                                     minHeight:'52px', padding:'4px 6px', border:'none', background:bg, borderRadius:'10px',
                                     color, cursor:'pointer', touchAction:'manipulation', textDecoration:'none', gap:'2px' };
                    const inner = <>
                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{flexShrink:0}}>{item.icon}</svg>
                        <span style={{fontSize:'11px', fontWeight:700, lineHeight:1.1}}>{item.label}</span>
                    </>;
                    return item.isLink
                        ? <a key={item.label} href={item.href} style={shared}>{inner}</a>
                        : <button key={item.label} onClick={item.onClick} style={shared}>{inner}</button>;
                })}
            </nav>

            {/* Spacer for fixed header */}
            <div style={{ height: 'calc(56px + max(env(safe-area-inset-top), 0px))' }} />

            {/* Billing modal */}
            {showBilling && (
                <Suspense fallback={null}>
                    <SubscriptionManagement onClose={() => setShowBilling(false)} />
                </Suspense>
            )}
        </>
    );
};

export default MobileNavigation;

